/* Copyright (c) 2022-2023, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { KeyboardEventKey, KeyboardEventType } from './keyboard-event';
import { PointerEventType } from './pointer-events';

function getKeyInfo(key: KeyboardEventKey): KeyboardEventInit {
    let keyCode: number;
    switch (key) {
        case 'ArrowUp':
            keyCode = 38;
            break;
        case 'ArrowDown':
            keyCode = 40;
            break;
        case 'ArrowLeft':
            keyCode = 37;
            break;
        case 'ArrowRight':
            keyCode = 39;
            break;
        case 'Enter':
            keyCode = 13;
            break;
        case 'Escape':
            keyCode = 27;
            break;
    }
    // Casts to `any` because compiler complains about the deprecated `keyCode`
    return { key, keyCode } as any;
}

/**
 * Gets the target to which synthesized events will be dispatched.
 */
export function getSpatialNavigationEventTarget(): EventTarget {
    return document.activeElement ?? document;
}

export function dispatchKeyboardEvent(target: EventTarget, event: KeyboardEventType, key: KeyboardEventKey) {
    if (target instanceof HTMLIFrameElement) {
        return;
    }

    target.dispatchEvent(
        new KeyboardEvent(event, {
            ...getKeyInfo(key),
            view: window,
            bubbles: true,
            cancelable: true
        })
    );
}

export function dispatchClickEvent(target: EventTarget) {
    if (target instanceof HTMLIFrameElement) {
        return;
    }

    target.dispatchEvent(
        new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true
        })
    );
}

export function isEventUserActivated(event: Event): boolean {
    return event?.isTrusted;
}
